import React, { useEffect, useRef, useState } from 'react';
import { RiArrowDownSLine, RiCalendarLine, RiRestaurantLine } from 'react-icons/ri';
import { COPY } from '../copy';
import { fmtShort } from '../lib/dates';
import { buildPresets, rangeLabel, validateRange } from '../lib/sufficiency';
import type { DateRange, Granularity } from '../types';
import { HelpBody, InfoTip } from './parts';

const VIEWS: { value: Granularity; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

export interface SufficiencyHeaderProps {
  titleId: string;
  granularity: Granularity;
  onGranularityChange: (g: Granularity) => void;
  /** The range on screen: the custom one, or the server's default echoed back. null while unknown. */
  range: DateRange | null;
  onRangeChange: (range: DateRange) => void;
  today: string;
  planWindow: { start: string; end: string } | null;
}

export function SufficiencyHeader({ titleId, granularity, onGranularityChange, range, onRangeChange, today, planWindow }: SufficiencyHeaderProps) {
  return (
    <div className="ns-top">
      <div className="ns-card-h">
        <span className="ns-ic-h" aria-hidden>
          <RiRestaurantLine />
        </span>
        <h3 id={titleId}>{COPY.title}</h3>
        <InfoTip label="how the score works" title={COPY.scoreHelp.title} wide>
          <HelpBody {...COPY.scoreHelp} />
        </InfoTip>
        <div className="ns-tools">
          <RangePicker range={range} onApply={onRangeChange} today={today} planWindow={planWindow} />
          <div className="ns-chips" role="tablist" aria-label="View">
            {VIEWS.map((v) => (
              <button
                key={v.value}
                type="button"
                role="tab"
                aria-selected={granularity === v.value}
                onClick={() => onGranularityChange(v.value)}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <p className="ns-sub">{COPY.subtitle}</p>
    </div>
  );
}

function RangePicker({
  range,
  onApply,
  today,
  planWindow,
}: {
  range: DateRange | null;
  onApply: (r: DateRange) => void;
  today: string;
  planWindow: { start: string; end: string } | null;
}) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const presets = buildPresets(today, planWindow);
  const invalid = validateRange(from, to, today);
  // Explain the block only once both dates are in; until then Apply just stays off.
  const error = from && to ? invalid : null;

  const close = (refocus = true) => {
    setOpen(false);
    if (refocus) buttonRef.current?.focus();
  };

  const toggle = () => {
    if (!open) {
      setFrom(range?.from ?? '');
      setTo(range?.to ?? '');
    }
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) close(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const commit = (r: DateRange) => {
    onApply(r);
    close();
  };

  const apply = () => {
    if (!invalid) commit({ from, to });
  };

  return (
    <div className="ns-rp" ref={rootRef}>
      <button ref={buttonRef} type="button" className="ns-range" aria-haspopup="dialog" aria-expanded={open} onClick={toggle}>
        <RiCalendarLine aria-hidden />
        {range ? rangeLabel(range) : 'Choose dates'}
        <RiArrowDownSLine aria-hidden className="ns-dn" />
      </button>
      {open && (
        <div className="ns-rp-pop" role="dialog" aria-label="Choose dates">
          <div className="ns-rp-pre">
            {presets.map((p) => (
              <button
                key={p.id}
                type="button"
                disabled={!p.range}
                aria-pressed={!!p.range && !!range && p.range.from === range.from && p.range.to === range.to}
                onClick={() => p.range && commit(p.range)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="ns-rp-in">
            <label>
              From
              <input type="date" value={from} max={today} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label>
              To
              <input type="date" value={to} max={today} onChange={(e) => setTo(e.target.value)} />
            </label>
          </div>
          <p className="ns-rp-err" role="alert">
            {error}
          </p>
          <div className="ns-rp-act">
            <button type="button" className="ns-rp-cancel" onClick={() => close()}>
              Cancel
            </button>
            <button type="button" className="ns-rp-apply" onClick={apply} disabled={!!invalid}>
              Apply
            </button>
          </div>
          {planWindow && <p className="ns-rp-note">Logs start on {fmtShort(planWindow.start)}, when the plan began.</p>}
        </div>
      )}
    </div>
  );
}
